import React, { createContext, useContext, useState, useEffect } from 'react';
import useDepartments from '../hooks/useDepartments';
import useCourses from '../hooks/useCourses';

const SearchContext = createContext();

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

export const SearchProvider = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const { departments } = useDepartments();
  const { courses: allCourses } = useCourses();

  const performSearch = (query) => {
    if (!query?.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    const lowerQuery = query.toLowerCase();

    try {
      // Search in courses
      const courseResults = (allCourses || []).filter((course) =>
        course?.title?.toLowerCase().includes(lowerQuery) ||
        course?.category?.toLowerCase().includes(lowerQuery) ||
        course?.description?.toLowerCase().includes(lowerQuery) ||
        course?.department?.toLowerCase().includes(lowerQuery)
      );

      // Search in departments
      const departmentResults = (departments || []).filter((dept) =>
        dept?.name?.toLowerCase().includes(lowerQuery) ||
        dept?.description?.toLowerCase().includes(lowerQuery)
      );

      // Combine results with type indicators
      const combinedResults = [
        ...courseResults.map((course) => ({ ...course, type: 'course' })),
        ...departmentResults.map((dept) => ({ ...dept, type: 'department' })),
      ];

      setSearchResults(combinedResults);
    } catch (error) {
      console.error('Error performing search:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
  };

  const value = {
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    performSearch,
    clearSearch,
    allCourses: allCourses || [],
    departments: departments || [],
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};